import { Channel } from "./Channel.ts";
import type { EventMap, WireMessage } from "./types.ts";

export class Socket {
  private ws: WebSocket | null = null;
  private endpoint: string;
  private channels = new Map<string, Channel<any>>();
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private intentionallyClosed = false;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  /** Connect to WebSocket server */
  connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    this.intentionallyClosed = false;

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.endpoint);
      this.ws = ws;

      ws.onopen = () => {
        console.log("WebSocket connected");
        this.clearReconnect();
        this.reconnectAttempts = 0;

        // Rejoin all channels on fresh connection
        Promise.allSettled(Array.from(this.channels.values()).map((ch) => ch.rejoin()));

        resolve();
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WireMessage;
          const channel = this.channels.get(msg.topic);
          if (channel) {
            channel.handleMessage(msg);
          }
        } catch (e) {
          console.error("Invalid message", e, event.data);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        // Let onclose drive reconnects; only reject connect() if not yet open
        if (ws.readyState !== WebSocket.OPEN) {
          reject(error);
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket closed", event.code);
        if (this.intentionallyClosed) return; // No reconnect
        if (!this.shouldReconnect(event)) return;
        this.scheduleReconnect();
      };
    });
  }

  /** Create/get channel for topic with typed events */
  channel<TEvents extends EventMap = Record<string, unknown>>(topic: string): Channel<TEvents> {
    if (this.channels.has(topic)) {
      return this.channels.get(topic)!;
    }

    const ch = new Channel<TEvents>(this, topic);
    this.channels.set(topic, ch);
    return ch;
  }

  /** Central send method (channels delegate here) */
  send(msg: WireMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("Dropping message; socket not open", msg);
      return;
    }
    this.ws.send(JSON.stringify(msg));
  }

  /** Disconnect from server */
  disconnect(): void {
    this.intentionallyClosed = true;
    this.clearReconnect();
    this.ws?.close(1000, "client disconnect");
    this.ws = null;
  }

  private shouldReconnect(event: CloseEvent): boolean {
    // No reconnect on normal closure (1000)
    return event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return; // Already scheduled

    const base = Math.min(1000 * 2 ** this.reconnectAttempts, 10000);
    const jitter = Math.random() * 250;
    const delay = base + jitter;

    this.reconnectAttempts++;

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      console.log(`Reconnecting (attempt ${this.reconnectAttempts})...`);
      this.connect().catch(() => {
        // onclose will schedule again if needed
      });
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
